import { BehaviorSubject, Observable } from 'rxjs';

import { HttpBackend, HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { environment } from '@environments/environment';
import { BreakpointService } from '@libs/breakpoints';
import { delay, distinctUntilChanged, filter, finalize, switchMap } from 'rxjs/operators';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
    private readonly http: HttpClient;
    private readonly rootDomain: string;
    private readonly market: string;
    private readonly msRoutes: string[];
    private readonly planPull = new BehaviorSubject<boolean>(false);

    private cacheKey?: string;

    constructor(
        private readonly handler: HttpBackend,
        private readonly serverTokens: ServerTokensService,
        private readonly breakpoints: BreakpointService,
        private readonly urlHelper: UrlHelperService
    ) {
        this.rootDomain = environment.production ? this.urlHelper.getRootDomain() : environment.apiDomain;
        this.market = this.urlHelper.getCurrentMarket;
        this.msRoutes = ['customers-ms', 'orders-ms', 'search-ms'];
        this.http = new HttpClient(this.handler);

        this.planPull
            .pipe(
                distinctUntilChanged(),
                filter(pull => pull),
                delay(60000),
                switchMap(() =>
                    this.http
                        .get(
                            `https://api.${this.rootDomain}/${this.market}/v1/content/lastpublishdate?v=${Date.now()}`,
                            {
                                responseType: 'text'
                            }
                        )
                        .pipe(finalize(() => this.planPull.next(false)))
                )
            )
            .subscribe({
                next: date => (this.cacheKey = this.cacheKey = this.getTimeCacheKey(date)),
                error: () => console.error('Failed to refresh cache-key')
            });
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const url: string = req.url;
        const cacheQuery: string = (url.indexOf('?') > -1 ? '&' : '?') + 'v=' + this.getCacheKey();
        const isMsRoute = this.msRoutes.some(msRoute => url.includes(msRoute));
        return next.handle(
            req.clone({
                url: `${url}${isMsRoute ? '' : cacheQuery}`
            })
        );
    }

    private getCacheKey(): string {
        if (this.breakpoints.isServer() || !this.http) {
            return this.getKey();
        }

        this.planPull.next(true);

        return this.getKey();
    }

    private getKey(): string {
        if (this.cacheKey) {
            return this.cacheKey;
        }
        this.cacheKey = this.serverTokens.cmsCacheKey;

        return this.cacheKey;
    }

    private getTimeCacheKey(date: string): string {
        return String(new Date(date).getTime());
    }
}
